import { gql } from "@apollo/client";

const HUB_SERIES_FIELDS = gql`
  fragment HubSeriesFields on IndicatorSeries {
    indicateurId
    libelle
    unit
    points {
      value
      unit
      periodStart
      territoryLabel
      mesureId
    }
  }
`;

/** Hub overview — published KPIs, land cover, insights (no change to dashboard query). */
export const HUB_OVERVIEW_QUERY = gql`
  query HubOverview(
    $territoryIds: [ID!]
    $dateFrom: String
    $dateTo: String
    $metric: String
    $erosionIndicateurId: ID!
    $ecoFrom: String
    $ecoTo: String
    $ecoCarboneId: ID!
    $ecoPfnlId: ID!
    $ecoEauId: ID!
  ) {
    dashboardKpis(filters: { territoryIds: $territoryIds, dateFrom: $dateFrom, dateTo: $dateTo }) {
      validatedMeasureCount
      indicatorWithDataCount
      coverageRatio
      territoryCount
      latestYear
      alertCount
      economicTotalVet
      economicUnit
      erosionPerceptionPct
      cultivatedVariationPct
      forestClearVariationPct
    }
    landCoverSeries(
      territoryIds: $territoryIds
      metric: $metric
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      unit
      points {
        mesureId
        periodStart
        value
        unit
        territoryId
        territoryLabel
        dimensions {
          type
          code
          label
        }
      }
    }
    dashboardInsights(
      filters: { territoryIds: $territoryIds, dateFrom: $dateFrom, dateTo: $dateTo }
    ) {
      id
      kind
      label
      severity
      algorithmOrQuery
      createdAt
    }
    erosionDistribution: indicatorDistribution(
      indicateurId: $erosionIndicateurId
      territoryIds: $territoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      indicateurId
      libelle
      sampleSize
      territoryLabel
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
    ecoCarbone: indicatorSeries(indicateurId: $ecoCarboneId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...HubSeriesFields
    }
    ecoPfnl: indicatorSeries(indicateurId: $ecoPfnlId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...HubSeriesFields
    }
    ecoEau: indicatorSeries(indicateurId: $ecoEauId, dateFrom: $ecoFrom, dateTo: $ecoTo) {
      ...HubSeriesFields
    }
  }
  ${HUB_SERIES_FIELDS}
`;

/** Latest published insights for the hub updates side panel. */
export const HUB_UPDATES_QUERY = gql`
  query HubUpdates($territoryIds: [ID!], $dateFrom: String, $dateTo: String) {
    dashboardInsights(
      filters: { territoryIds: $territoryIds, dateFrom: $dateFrom, dateTo: $dateTo }
    ) {
      id
      kind
      label
      severity
      algorithmOrQuery
      createdAt
    }
  }
`;

export const HUB_OVERVIEW_VARS = {
  territoryIds: ["nbs-zon-madina-oula"] as string[],
  dateFrom: "2005-01-01",
  dateTo: "2025-12-31",
  metric: "superficie",
  erosionIndicateurId: "nbs-ind-distribution-erosion",
  ecoFrom: "2014-01-01",
  ecoTo: "2014-12-31",
  ecoCarboneId: "nbs-ind-eco-regulation-climat-global",
  ecoPfnlId: "nbs-ind-eco-pfnl-total",
  ecoEauId: "nbs-ind-eco-eau-autoconsommation",
} as const;

/**
 * Hub services — same eco series as story step 6, but scoped to the navbar
 * landscape (`territoryIds`) so values change with the selected territoire.
 */
export const HUB_SERVICES_QUERY = gql`
  query HubEcosystemServices(
    $territoryIds: [ID!]
    $dateFrom: String
    $dateTo: String
    $ecoFrom: String
    $ecoTo: String
    $vetId: ID!
    $ecoUsageId: ID!
    $ecoOptionId: ID!
    $ecoNonUsageId: ID!
    $ecoPfnlId: ID!
    $ecoPecheId: ID!
    $ecoBoisId: ID!
    $ecoChasseId: ID!
    $ecoAgricultureId: ID!
    $ecoMicroclimatId: ID!
    $ecoGenetiquesId: ID!
    $ecoEauId: ID!
    $ecoRegLocaleId: ID!
    $ecoRegClimatGlobalId: ID!
    $ecoServicesCulturelsId: ID!
    $ecoEcotourismeId: ID!
  ) {
    dashboardKpis(filters: { territoryIds: $territoryIds, dateFrom: $dateFrom, dateTo: $dateTo }) {
      cultivatedVariationPct
      forestClearVariationPct
      erosionPerceptionPct
      latestYear
    }
    vet: indicatorSeries(
      indicateurId: $vetId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoUsage: indicatorSeries(
      indicateurId: $ecoUsageId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoOption: indicatorSeries(
      indicateurId: $ecoOptionId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoNonUsage: indicatorSeries(
      indicateurId: $ecoNonUsageId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoPfnl: indicatorSeries(
      indicateurId: $ecoPfnlId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoPeche: indicatorSeries(
      indicateurId: $ecoPecheId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoBois: indicatorSeries(
      indicateurId: $ecoBoisId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoChasse: indicatorSeries(
      indicateurId: $ecoChasseId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoAgriculture: indicatorSeries(
      indicateurId: $ecoAgricultureId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoMicroclimat: indicatorSeries(
      indicateurId: $ecoMicroclimatId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoGenetiques: indicatorSeries(
      indicateurId: $ecoGenetiquesId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoEau: indicatorSeries(
      indicateurId: $ecoEauId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoRegLocale: indicatorSeries(
      indicateurId: $ecoRegLocaleId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoRegClimatGlobal: indicatorSeries(
      indicateurId: $ecoRegClimatGlobalId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoServicesCulturels: indicatorSeries(
      indicateurId: $ecoServicesCulturelsId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
    ecoEcotourisme: indicatorSeries(
      indicateurId: $ecoEcotourismeId
      territoryIds: $territoryIds
      dateFrom: $ecoFrom
      dateTo: $ecoTo
    ) {
      ...HubSeriesFields
    }
  }
  ${HUB_SERIES_FIELDS}
`;

/** Field-collection prototype: services + their published indicators. */
export const HUB_COLLECT_CATALOG_QUERY = gql`
  query HubCollectCatalog {
    indicatorFilterOptions {
      services {
        id
        libelle
        categorie
        prioritaire
      }
    }
    indicatorCatalog(first: 500, offset: 0, sort: "libelle_asc") {
      nodes {
        id
        libelle
        serviceId
        unite
      }
    }
  }
`;

export const HUB_SERVICES_QUERY_VARS = {
  vetId: "nbs-ind-eco-valeur-totale-vet",
  ecoUsageId: "nbs-ind-eco-valeur-usage-totale",
  ecoOptionId: "nbs-ind-eco-valeur-option",
  ecoNonUsageId: "nbs-ind-eco-valeur-non-usage",
  ecoPfnlId: "nbs-ind-eco-pfnl-total",
  ecoPecheId: "nbs-ind-eco-peche-continentale",
  ecoBoisId: "nbs-ind-eco-bois-energie-construction",
  ecoChasseId: "nbs-ind-eco-chasse-viande-brousse",
  ecoAgricultureId: "nbs-ind-eco-agriculture-fruits-foret",
  ecoMicroclimatId: "nbs-ind-eco-elevage-microclimat",
  ecoGenetiquesId: "nbs-ind-eco-ressources-genetiques",
  ecoEauId: "nbs-ind-eco-eau-autoconsommation",
  ecoRegLocaleId: "nbs-ind-eco-regulation-locale",
  ecoRegClimatGlobalId: "nbs-ind-eco-regulation-climat-global",
  ecoServicesCulturelsId: "nbs-ind-eco-services-culturels",
  ecoEcotourismeId: "nbs-ind-eco-ecotourisme-esthetique",
} as const;
